import { inject } from '@angular/core';
import { InMemoryCacheService } from 'src/app/common/cache/in-memory-cache.service';
import { Injectable } from '@angular/core';
import { CoreService } from 'src/app/common/http-services/core.service';
import { environment } from 'src/environments/environment';
import { KeywordsForSite, KeywordSuggestionsRequest, RankedKeywordsRequest, RelatedKeywordRequest } from './models/keyword-search-request';
import { PageLinksRequest, PageRequest, PagesByResourceRequest, ResourceRequest, SiteScanRequest } from './models/on-page';
import { AnchorsRequest, BacklinksHistoryRequest, BacklinksRequest, DomainIntersectionRequest, ReferringDomainRequest, HistoricalRankOverviewRequest, AmazonProductKeywordIntersectionRequest, GoogleDomainIntersectionRequest, BingDomainIntersectionRequest } from './models/site-search-request';
import { LocalCompetitorIntelligenceResponse, LocalCompetitorPdfExportResponse, LocalGeoGridGenerateRequest, LocalGeoGridGenerateResponse, LocalGeoGridSummary, LocalGeoGridValidateRequest, LocalGeoGridValidateResponse, LocalKeywordCoverageMatrixResponse, LocalKeywordDetailResponse, LocalKeywordTrackerResponse, LocalSeoDashboardSummaryResponse, LocalSeoRecommendationsResponse, LocalSnapshot, LocalVisibilityTrendResponse } from './models/local-seo';
import { OrganicKeywordHistoryResponse, OrganicRankRecommendationsResponse, OrganicRankRefreshRequest, OrganicRankRefreshResponse, OrganicRankSnapshotResponse, OrganicTrendSummaryResponse } from './models/organic-rank-history';
import { UserList } from './models/user-list';
import { UserListType } from './models/user-list-type';
import { MessageType, UtilsService } from 'src/app/common';
import { catchError, firstValueFrom, Observable, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { AmazonRankedKeywordsRequest, BingRankedKeywordsRequest } from './models/paid-ads';
import { SeoDashboardRequest, SeoDashboardResponse } from '../dashboard/dashboard-read-model';
@Injectable({
  providedIn: 'root'
})
export class SeoApiService {

  private _backlinksSummary: any = {};
  private _backlinksDomainIntersection: any = {};
  private _backlinksPageIntersection: any = {};
  private _backlinksDomainPages: any = {};
  private _backlinksReferringDomains: any = {};
  private _backlinksReferringNetworks: any = {};
  private _AISummarization: any = {};
  private _backlinks: any = {};
  private _backlinksAnchors: any = {};
  private _backlinksHistory: any = {};
  private _keywordIdeas: any = {};
  private _keywordSuggestions: any = {};
  private _keywordRelatedKeywords: any = {};
  private _bingRelatedKeywords: any = {};
  private _amazonRelatedKeywords: any = {};
  private _rankedKeywords: any = {};
  private _bingRankedKeywords: any = {};
  private _amazonRankedKeywords: any = {};
  private _keywordsForSite: any = {};
  private _serpKeywords: any = {};
  private _domainWhois: any = {};
  private _competitorsDomain: any = {};
  private _domainIntersection: any = {};
  private _bingDomainIntersection: any = {};
  private _amazonProductKeywordIntersections: any = {};
  private _pageIntersection: any = {};
  private _domainRankings: any = {};
  private _historicalRankOverview: any = {};
  private _historicalSerps: any = {};
  private _historicalSearchVolume: any = {};
  private _serps: any = {};
  private _organicRankSnapshot: any = {};
  private _organicKeywordHistory: any = {};
  private _organicTrendSummary: any = {};
  private _organicRankRecommendations: any = {};
  private _contentAnalysisCategories: any = null;
  private _contentAnalysisLocations: any = null;
  private _contentAnalysisLanguages: any = null;
  private _relevantPages: any = {};

  private readonly brandProjectCache = inject(InMemoryCacheService);
  private _categoryTrends: any = {};
  private _ratingDistribution: any = {};
  private _pharseTrends: any = {};
  private _summary: any = {};

  private _webMentionOverview: any = {};
  private _webMentionSummary: any = {};


  constructor(private core: CoreService<{}>, private utils: UtilsService, private http: HttpClient) {

  }

  fetchCountries(): Observable<any> {
    return this.http.get('https://unpkg.com/world-atlas@2.0.2/countries-50m.json');
  }

  public async getDashboardOverview(payload: SeoDashboardRequest, handleError: boolean = false) {
    const response: { data: SeoDashboardResponse | null; error: unknown | null } = { data: null, error: null };

    try {
      response.data = await firstValueFrom(
        this.core.post(`${environment.seoUrl}/dashboard/overview`, payload)
      ) as SeoDashboardResponse;
    } catch (error) {
      response.error = error;
      await this.handleError(error, handleError);
    }

    return response;
  }

  public async getBacklinksDomainIntersection(payload: DomainIntersectionRequest, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._backlinksDomainIntersection[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/backlinks/domainIntersection`, payload));
        this._backlinksDomainIntersection[hashKey] = response.data;
      } else {
        response.data = this._backlinksDomainIntersection[hashKey];
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getBacklinksPageIntersection(payload: any, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._backlinksPageIntersection[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/backlinks/pageIntersection`, payload));
        this._backlinksPageIntersection[hashKey] = response.data;
      } else {
        response.data = this._backlinksPageIntersection[hashKey];
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getBacklinksDomainPages(payload: any, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._backlinksDomainPages[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/backlinks/domainPages`, payload));
        this._backlinksDomainPages[hashKey] = response.data;
      } else {
        response.data = this._backlinksDomainPages[hashKey];
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getGMBAudit(payload: any, handleError: boolean = false) {
    const response = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/BusinessData/gmbData`, payload));
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getBacklinksReferringDomains(payload: ReferringDomainRequest, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._backlinksReferringDomains[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/backlinks/referringDomains`, payload));
        this._backlinksReferringDomains[hashKey] = response.data;
      } else {
        response.data = this._backlinksReferringDomains[hashKey];
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }
    return response;
  }

  public async getBacklinksReferringNetworks(payload: any, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._backlinksReferringNetworks[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/backlinks/referringNetworks`, payload));
        this._backlinksReferringNetworks[hashKey] = response.data;
      } else {
        response.data = this._backlinksReferringNetworks[hashKey];
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }
    return response;
  }

  public async getBacklinksSummary(payload: BacklinksRequest, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._backlinksSummary[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/backlinks/summary`, payload));
        this._backlinksSummary[hashKey] = response.data;
      } else {
        response.data = this._backlinksSummary[hashKey];
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getBacklinks(payload: BacklinksRequest, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._backlinks[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/backlinks/backlinks`, payload));
        this._backlinks[hashKey] = response.data;
      } else {
        response.data = this._backlinks[hashKey];
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getBacklinksAnchors(payload: AnchorsRequest, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._backlinksAnchors[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/backlinks/anchors`, payload));
        this._backlinksAnchors[hashKey] = response.data;
      } else {
        response.data = this._backlinksAnchors[hashKey];
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }
    return response;
  }

  public async getBacklinksHistory(payload: BacklinksHistoryRequest, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._backlinksHistory[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/backlinks/history`, payload));
        this._backlinksHistory[hashKey] = response.data;
      } else {
        response.data = this._backlinksHistory[hashKey];
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getKeywordsIdeas(payload: any, provider : string, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._keywordIdeas[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/keywords/${provider}/keywordIdeas`, payload));
        this._keywordIdeas[hashKey] = response.data;
      } else {
        response.data = this._keywordIdeas[hashKey];
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getKeywordsSuggestions(payload: KeywordSuggestionsRequest, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._keywordSuggestions[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/keywords/google/keywordSuggestions`, payload));
        this._keywordSuggestions[hashKey] = response.data;
      } else {
        response.data = this._keywordSuggestions[hashKey];;
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getRelatedKeywords(payload: RelatedKeywordRequest, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._keywordRelatedKeywords[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/keywords/google/relatedkeywords`, payload));
        this._keywordRelatedKeywords[hashKey] = response.data;
      } else {
        response.data = this._keywordRelatedKeywords[hashKey];
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getBingRelatedKeywords(payload: RelatedKeywordRequest, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._bingRelatedKeywords[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/bing/relatedKeywords`, payload));
        this._bingRelatedKeywords[hashKey] = response.data;
      } else {
        response.data = this._bingRelatedKeywords[hashKey]
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
    }
    public async getAmazonRelatedKeywords(payload: RelatedKeywordRequest, handleError: boolean = false) {
        const response = { data: null, error: null };
        const hashKey = this.utils.hashPayload(payload);
        const data = this._amazonRelatedKeywords[hashKey];

        try {
          if (!data) {
            response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/amazon/relatedKeywords`, payload));
            this._amazonRelatedKeywords[hashKey] = response.data;
          } else {
            response.data = this._amazonRelatedKeywords[hashKey];
          }
        } catch (error) {
          response.error = error;
          await this.handleError(error,handleError);
        }

        return response;
      }

  public async getRankedKeywords(payload: RankedKeywordsRequest, handleError: boolean = false, useCache: boolean = true) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = useCache ? this._rankedKeywords[hashKey] : null;

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/competitors/google/rankedKeywords`, payload));
        this._rankedKeywords[hashKey] = response.data;
      } else {
        response.data = this._rankedKeywords[hashKey];
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }


    return response;
  }

  public async getBingRankedKeywords(payload: BingRankedKeywordsRequest, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._bingRankedKeywords[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/bing/rankedKeywords`, payload));
        this._bingRankedKeywords[hashKey] = response.data;
      } else {
        response.data = this._bingRankedKeywords[hashKey];
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }


    return response;
    }
    public async getAmazonRankedKeywords(payload: AmazonRankedKeywordsRequest, handleError: boolean = false) {
        const response = { data: null, error: null };
        const hashKey = this.utils.hashPayload(payload);
        const data = this._amazonRankedKeywords[hashKey];

        try {
          if (!data) {
            response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/amazon/rankedKeywords`, payload));
            this._amazonRankedKeywords[hashKey] = response.data;
          } else {
            response.data = this._amazonRankedKeywords[hashKey];
          }
        } catch (error) {
          response.error = error;
          await this.handleError(error,handleError);
          }

        return response;
      }

  public async getKeywordsForSite(payload: KeywordsForSite, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._keywordsForSite[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/keywords/google/keywordsForSite`, payload));
        this._keywordsForSite[hashKey] = response.data;
      } else {
        response.data = this._keywordsForSite[hashKey];
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getSerpKeywords(payload: any, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._serpKeywords[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/competitorss/google/serpCompetitors`, payload));
        this._serpKeywords[hashKey] = response.data;
      } else {
        response.data = this._serpKeywords[hashKey];
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getDomainWhois(payload: any, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._domainWhois[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/dataforseo_labs/domain_whois_overview`, payload));
        this._domainWhois[hashKey] = response.data;
      } else {
        response.data = this._domainWhois[hashKey];;
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  //SENTIMENT

  private brandRequestCacheKey(payload: unknown): string {
    const weekStart = new Date();
    weekStart.setUTCHours(0, 0, 0, 0);
    weekStart.setUTCDate(weekStart.getUTCDate() - ((weekStart.getUTCDay() + 6) % 7));
    return this.utils.hashPayload({ projectId: this.brandProjectCache.selectedProject?.id, week: weekStart.toISOString(), payload });
  }
  public async categoryTrends(payload: any, handleError: boolean = false) {
      payload = { ...payload, projectId: this.brandProjectCache.selectedProject?.id };
    const response = { data: null, error: null };
    const hashKey = this.brandRequestCacheKey(payload);
    const data = this._categoryTrends[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/ContentAnalysis/categoryTrends?projectId=${encodeURIComponent(this.brandProjectCache.selectedProject?.id ?? "")}`, payload));
        this._categoryTrends[hashKey] = response.data;
      } else {
        response.data = this._categoryTrends[hashKey];
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async phraseTrends(payload: any, handleError: boolean =false) {
      payload = { ...payload, projectId: this.brandProjectCache.selectedProject?.id };
    const response = { data: null, error: null };
    const hashKey = this.brandRequestCacheKey(payload);
    const data = this._pharseTrends[hashKey];

    try {
        if(!data) {
            response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/contentAnalysis/phraseTrends?projectId=${encodeURIComponent(this.brandProjectCache.selectedProject?.id ?? "")}`, payload));
            this._pharseTrends[hashKey] = response.data;
        } else {
            response.data = this._pharseTrends[hashKey];
        }
    } catch(error) {
        response.error = error;
    }

    return response;

  }

  public async ratingDistribution(payload: any, handleError: boolean =false) {
      payload = { ...payload, projectId: this.brandProjectCache.selectedProject?.id };
    const response = { data: null, error: null };
    const hashKey = this.brandRequestCacheKey(payload);
    const data = this._ratingDistribution[hashKey];

    try{
        if(!data) {
            response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/contentAnalysis/ratingDistribution?projectId=${encodeURIComponent(this.brandProjectCache.selectedProject?.id ?? "")}`, payload));
            this._ratingDistribution[hashKey] = response.data;
        } else {
            response.data = this._ratingDistribution[hashKey];
        }
    } catch(error) {
        response.error = error;
    }

    return response;
  }

  public async summary(payload: any, handleError: boolean =false) {
      payload = { ...payload, projectId: this.brandProjectCache.selectedProject?.id };
    const response = { data: null, error: null };
    const hashKey = this.brandRequestCacheKey(payload);
    const data = this._summary[hashKey];

    try {
        if(!data) {
            response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/contentAnalysis/sentimentAnalysis?projectId=${encodeURIComponent(this.brandProjectCache.selectedProject?.id ?? "")}`, payload));
            this._summary[hashKey] = response.data;
        } else {
            response.data = this._summary[hashKey];
        }
    } catch(error) {
        response.error = error;
    }

    return response;
  }



  /**
   * WEB MENTION API'S
   **/

  public async webMentionOverview(payload: any, handleError: boolean = false) {
      payload = { ...payload, projectId: this.brandProjectCache.selectedProject?.id };
    const response = { data: null, error: null };
    const hashKey = this.brandRequestCacheKey(payload);
    const data = this._webMentionOverview[hashKey];

    try {
        if(!data) {
            response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/contentAnalysis/search?projectId=${encodeURIComponent(this.brandProjectCache.selectedProject?.id ?? "")}`, payload));
            this._webMentionOverview[hashKey] = response.data;
        } else {
            response.data = this._webMentionOverview[hashKey];
        }
    } catch (error) {
        response.error = error;
    }

    return response;
  }

  public async webMentionSummary(payload: any, handleError: boolean = false) {
      payload = { ...payload, projectId: this.brandProjectCache.selectedProject?.id };
    const response = { data: null, error: null };
    const hashKey = this.brandRequestCacheKey(payload);
    const data = this._webMentionSummary[hashKey];

    try {
        if(!data) {
            response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/contentAnalysis/summary?projectId=${encodeURIComponent(this.brandProjectCache.selectedProject?.id ?? "")}`, payload));
            this._webMentionSummary[hashKey] = response.data;
        } else {
            response.data = this._webMentionSummary[hashKey];
        }
    } catch (error) {
        response.error = error;
    }

    return response;
  }

  public async getCategories() {
    const response = { data: null, error: null };
    const localStorageKey = 'contentAnalysisCategories';

    try {
      // Check local storage first
      const cachedData = localStorage.getItem(localStorageKey);
      if (cachedData) {
        response.data = JSON.parse(cachedData);
        this._contentAnalysisCategories = response.data;
      } else if (this._contentAnalysisCategories) {
        // Check in-memory cache
        response.data = this._contentAnalysisCategories;
      } else {
        // Fetch from API
        response.data = this._contentAnalysisCategories = await firstValueFrom(this.core.get(`${environment.seoUrl}/SeoSettings/contentAnalysis/categories`));
        localStorage.setItem(localStorageKey, JSON.stringify(response.data));
      }
    } catch (error) {
      response.error = error;
    }

    return response;
  }

  public async getLocation() {
    const response = { data: null, error: null };
    const localStorageKey = 'contentAnalysisLocations';

    try {
      // Check local storage first
      const cachedData = localStorage.getItem(localStorageKey);
      if (cachedData) {
        response.data = JSON.parse(cachedData);
        this._contentAnalysisLocations = response.data;
      } else if (this._contentAnalysisLocations) {
        // Check in-memory cache
        response.data = this._contentAnalysisLocations;
      } else {
        // Fetch from API
        response.data = this._contentAnalysisLocations = await firstValueFrom(this.core.get(`${environment.seoUrl}/SeoSettings/contentAnalysis/locations`));
        localStorage.setItem(localStorageKey, JSON.stringify(response.data));
      }
    } catch (error) {
      response.error = error;
    }

    return response;
  }

  public async getLanguages() {
    const response = { data: null, error: null };
    const localStorageKey = 'contentAnalysisLanguages';

    try {
      // Check local storage first
      const cachedData = localStorage.getItem(localStorageKey);
      if (cachedData) {
        response.data = JSON.parse(cachedData);
        this._contentAnalysisLanguages = response.data;
      } else if (this._contentAnalysisLanguages) {
        // Check in-memory cache
        response.data = this._contentAnalysisLanguages;
      } else {
        // Fetch from API
        response.data = this._contentAnalysisLanguages = await firstValueFrom(this.core.get(`${environment.seoUrl}/SeoSettings/contentAnalysis/languages`));
        localStorage.setItem(localStorageKey, JSON.stringify(response.data));
      }
    } catch (error) {
      response.error = error;
    }

    return response;
  }


  public async getCompetitorsDomain(payload: any, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._competitorsDomain[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/competitors/google/competitorsDomain`, payload));
        this._competitorsDomain[hashKey] = response.data;
      } else {
        response.data = this._competitorsDomain[hashKey];
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getDomainIntersection(payload: GoogleDomainIntersectionRequest, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._domainIntersection[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/competitors/google/domainIntersection`, payload));
        this._domainIntersection[hashKey] = response.data;
      } else {
        response.data = this._domainIntersection[hashKey];
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getBingDomainIntersection(payload: BingDomainIntersectionRequest, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._bingDomainIntersection[hashKey];

    try {
        if(!data) {
            response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/bing/domainIntersection`, payload));
            this._bingDomainIntersection[hashKey] = response.data;
        } else {
            response.data = this._bingDomainIntersection[hashKey];
        }
    } catch (error) {
        response.error = error;
        if(handleError) {
            await this.handleError(error);
        }
    }

    return response;
  }

  public async getAmazonProductIntersection(payload: AmazonProductKeywordIntersectionRequest, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._amazonProductKeywordIntersections[hashKey];

    try{
        if(!data) {
            response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/amazon/productKeywordIntersections`, payload));
            this._amazonProductKeywordIntersections[hashKey] = response.data;
        } else {
            response.data = this._amazonProductKeywordIntersections[hashKey];
        }
    } catch(error) {
        response.error = error;
        if(handleError) {
            await this.handleError(error);
        }
    }

    return response;

  }

  public async getPageIntersection(payload: any, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._pageIntersection[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/competitors/google/pageIntersection`, payload));
        this._pageIntersection[hashKey] = response.data;
      } else {
        response.data = this._pageIntersection[hashKey];
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }


    return response;
  }

  public async getDomainRankings(payload: any, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._domainRankings[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/competitors/google/domainRankOverview`, payload));
        this._domainRankings[hashKey] = response.data;
      } else {
        response.data = this._domainRankings[hashKey];
      }
    } catch (error) {
      response.error = error;
      if (handleError) {
        await this.handleError(error);
      }
    }

    return response;
  }

  public async getHistoricalRankOverview(payload: HistoricalRankOverviewRequest, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._historicalRankOverview[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/competitors/google/historicalRankOverview`, payload));
        this._historicalRankOverview[hashKey] = response.data;
      } else {
        response.data = this._historicalRankOverview[hashKey];
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getAuditEvidence(scanId: number, payload: import('./models/on-page').AuditEvidenceRequest): Promise<{ data: import('./models/on-page').AuditEvidenceResult | null; error: unknown }> {
    try {
      const data = await firstValueFrom(this.core.post(
        `${environment.seoUrl}/pagescan/evidence/${scanId}`, payload)) as import('./models/on-page').AuditEvidenceResult;
      return { data, error: null };
    } catch (error) { return { data: null, error }; }
  }

  public async getAuditScan(scanId: number): Promise<{ data: import('./models/on-page').AuditScanContext | null; error: unknown }> {
    try {
      const data = await firstValueFrom(this.core.get(
        `${environment.seoUrl}/pagescan/context/${scanId}`)) as import('./models/on-page').AuditScanContext;
      return { data, error: null };
    } catch (error) { return { data: null, error }; }
  }

  public async getPageSummary(scanId: number, handleError: boolean = false) {
    const response = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.seoUrl}/pagescan/summary/${scanId}`));
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getPages(payload: PageRequest, scanId: number, handleError: boolean = false) {
    const response = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/pagescan/pages/${scanId}`, payload));
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getPageResources(payload: ResourceRequest, scanId: number, handleError: boolean = false) {
    const response = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/pagescan/resources/${scanId}`, payload));
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getPagesByResource(payload: PagesByResourceRequest, scanId: number, handleError: boolean = false) {
    const response = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/pagescan/pagesByResource/${scanId}`, payload));
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getPageLinks(payload: PageLinksRequest, scanId: number, handleError: boolean = false) {
    const response = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/pagescan/links/${scanId}`, payload));
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async performSiteScan(payload: SiteScanRequest, projectId: string, userId: string, handleError: boolean = false) {
    const response = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/onpage/sitescan/${projectId}/${userId}`, payload));
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getPageSpeedInsights(url: string, strategy: string, handleError: boolean = false, refresh: boolean = false) {
    const response = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.seoUrl}/lighthouse?url=${encodeURIComponent(url)}&strategy=${strategy}&refresh=${refresh}`));
    } catch (error) {
      response.error = error;
      if (handleError) {
        await this.handleError(error);
      }
    }

    return response;
  }

  public async getUserListByUser(id: string, type: UserListType, handleError: boolean = false) {
    const response = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.seoUrl}/${this.getUserListPath(type)}/user/${id}`));
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getUserListByProject(id: string, type: UserListType, handleError: boolean = false) {
    const response = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.seoUrl}/${this.getUserListPath(type)}/project/${id}/type/${type}`));
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getListById(id: string, type: UserListType, handleError: boolean = false) {
    const response = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.seoUrl}/${this.getUserListPath(type)}/${id}`));
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async createUserList(payload: UserList, type: UserListType, handleError: boolean = false) {
    const response = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/${this.getUserListPath(type)}`, payload));
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async updateUserList(payload: any, type: UserListType, handleError: boolean = false) {
    const response = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.bulkPut(`${environment.seoUrl}/${this.getUserListPath(type)}`, payload));
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async deleteUserList(id: string, type: UserListType, userId: string, handleError: boolean = false) {
    const response = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.delete(`${environment.seoUrl}/${this.getUserListPath(type)}/${id}/${userId}`));
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getLatestScan(projectId: string, handleError: boolean = false) {
    const response = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.seoUrl}/scan/latestScan/${projectId}`));
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getHistoricalSerps(payload: any, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._historicalSerps[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/competitors/google/historicalSerps`, payload));
        this._historicalSerps[hashKey] = response.data;
      } else {
        response.data = this._historicalSerps[hashKey];
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getHistoricalSearchVolume(payload: any, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._historicalSearchVolume[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/keywords/google/historicalSearchVolume`, payload));
        this._historicalSearchVolume[hashKey] = response.data;
      } else {
        response.data = this._historicalSearchVolume[hashKey];
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getSerps(payload: any, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._serps[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/serps/google/organicRegular`, payload));
        this._serps[hashKey] = response.data;
      } else {
        response.data = this._serps[hashKey];
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  public async getLocalKeywordTrackerByProject(projectId: string, snapshotLimit: number = 500, handleError: boolean = false): Promise<{ data: LocalKeywordTrackerResponse | null; error: unknown }> {
    const response: { data: LocalKeywordTrackerResponse | null; error: unknown } = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.seoUrl}/serps/google/localTracker/project/${encodeURIComponent(projectId)}?snapshotLimit=${snapshotLimit}`)) as LocalKeywordTrackerResponse;
    } catch (error) {
      response.error = error;
      await this.handleError(error, handleError);
    }

    return response;
  }

  public async getLocalKeywordDetailByProject(projectId: string, keyword: string, snapshotLimit: number = 500, handleError: boolean = false): Promise<{ data: LocalKeywordDetailResponse | null; error: unknown }> {
    const response: { data: LocalKeywordDetailResponse | null; error: unknown } = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.seoUrl}/serps/google/localKeywordDetail/project/${encodeURIComponent(projectId)}/keyword/${encodeURIComponent(keyword)}?snapshotLimit=${snapshotLimit}`)) as LocalKeywordDetailResponse;
    } catch (error) {
      response.error = error;
      await this.handleError(error, handleError);
    }

    return response;
  }

  public async getLocalGeoGridSummaryByProject(projectId: string, keyword: string | null = null, snapshotLimit: number = 1000, handleError: boolean = false): Promise<{ data: LocalGeoGridSummary | null; error: unknown }> {
    const response: { data: LocalGeoGridSummary | null; error: unknown } = { data: null, error: null };
    const keywordQuery = keyword ? `&keyword=${encodeURIComponent(keyword)}` : '';

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.seoUrl}/serps/google/geoGridSummary/project/${encodeURIComponent(projectId)}?snapshotLimit=${snapshotLimit}${keywordQuery}`)) as LocalGeoGridSummary;
    } catch (error) {
      response.error = error;
      await this.handleError(error, handleError);
    }

    return response;
  }

  public async getLocalDashboardSummaryByProject(projectId: string, snapshotLimit: number = 500, handleError: boolean = false): Promise<{ data: LocalSeoDashboardSummaryResponse | null; error: unknown }> {
    const response: { data: LocalSeoDashboardSummaryResponse | null; error: unknown } = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.seoUrl}/serps/google/localDashboardSummary/project/${encodeURIComponent(projectId)}?snapshotLimit=${snapshotLimit}`)) as LocalSeoDashboardSummaryResponse;
    } catch (error) {
      response.error = error;
      await this.handleError(error, handleError);
    }

    return response;
  }

  public async getLocalVisibilityTrendByProject(projectId: string, snapshotLimit: number = 500, competitorLimit: number = 100, handleError: boolean = false): Promise<{ data: LocalVisibilityTrendResponse | null; error: unknown }> {
    const response: { data: LocalVisibilityTrendResponse | null; error: unknown } = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.seoUrl}/serps/google/localVisibilityTrend/project/${encodeURIComponent(projectId)}?snapshotLimit=${snapshotLimit}&competitorLimit=${competitorLimit}`)) as LocalVisibilityTrendResponse;
    } catch (error) {
      response.error = error;
      await this.handleError(error, handleError);
    }

    return response;
  }

  public async getLocalSeoRecommendationsByProject(projectId: string, snapshotLimit: number = 500, competitorLimit: number = 100, handleError: boolean = false): Promise<{ data: LocalSeoRecommendationsResponse | null; error: unknown }> {
    const response: { data: LocalSeoRecommendationsResponse | null; error: unknown } = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.seoUrl}/serps/google/localSeoRecommendations/project/${encodeURIComponent(projectId)}?snapshotLimit=${snapshotLimit}&competitorLimit=${competitorLimit}`)) as LocalSeoRecommendationsResponse;
    } catch (error) {
      response.error = error;
      await this.handleError(error, handleError);
    }

    return response;
  }

  public async getLocalCompetitorIntelligenceByProject(projectId: string, snapshotLimit: number = 500, handleError: boolean = false): Promise<{ data: LocalCompetitorIntelligenceResponse | null; error: unknown }> {
    const response: { data: LocalCompetitorIntelligenceResponse | null; error: unknown } = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.seoUrl}/serps/google/localCompetitorIntelligence/project/${encodeURIComponent(projectId)}?snapshotLimit=${snapshotLimit}`)) as LocalCompetitorIntelligenceResponse;
    } catch (error) {
      response.error = error;
      await this.handleError(error, handleError);
    }

    return response;
  }

  public async getLocalKeywordCoverageMatrixByProject(projectId: string, snapshotLimit: number = 500, handleError: boolean = false): Promise<{ data: LocalKeywordCoverageMatrixResponse | null; error: unknown }> {
    const response: { data: LocalKeywordCoverageMatrixResponse | null; error: unknown } = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.seoUrl}/serps/google/localKeywordCoverageMatrix/project/${encodeURIComponent(projectId)}?snapshotLimit=${snapshotLimit}`)) as LocalKeywordCoverageMatrixResponse;
    } catch (error) {
      response.error = error;
      await this.handleError(error, handleError);
    }

    return response;
  }

  public async getLocalCompetitorPdfExportByProject(projectId: string, snapshotLimit: number = 500, competitorLimit: number = 100, handleError: boolean = false): Promise<{ data: LocalCompetitorPdfExportResponse | null; error: unknown }> {
    const response: { data: LocalCompetitorPdfExportResponse | null; error: unknown } = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.seoUrl}/serps/google/localCompetitorPdfExport/project/${encodeURIComponent(projectId)}?snapshotLimit=${snapshotLimit}&competitorLimit=${competitorLimit}`)) as LocalCompetitorPdfExportResponse;
    } catch (error) {
      response.error = error;
      await this.handleError(error, handleError);
    }

    return response;
  }

  public async getOrganicRankSnapshotByProject(projectId: string, snapshotLimit: number = 12, handleError: boolean = false): Promise<{ data: OrganicRankSnapshotResponse | null; error: any }> {
    const response: { data: OrganicRankSnapshotResponse | null; error: any } = { data: null, error: null };
    const payload = { projectId, snapshotLimit };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._organicRankSnapshot[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.get(`${environment.seoUrl}/serps/google/organicRankSnapshot/project/${encodeURIComponent(projectId)}?snapshotLimit=${snapshotLimit}`)) as OrganicRankSnapshotResponse;
        this._organicRankSnapshot[hashKey] = response.data;
      } else {
        response.data = this._organicRankSnapshot[hashKey] as OrganicRankSnapshotResponse;
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error, handleError);
    }

    return response;
  }

  public async getOrganicKeywordHistoryByProject(projectId: string, keyword: string, snapshotLimit: number = 24, handleError: boolean = false): Promise<{ data: OrganicKeywordHistoryResponse | null; error: any }> {
    const response: { data: OrganicKeywordHistoryResponse | null; error: any } = { data: null, error: null };
    const payload = { projectId, keyword, snapshotLimit };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._organicKeywordHistory[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.get(`${environment.seoUrl}/serps/google/organicKeywordHistory/project/${encodeURIComponent(projectId)}/keyword/${encodeURIComponent(keyword)}?snapshotLimit=${snapshotLimit}`)) as OrganicKeywordHistoryResponse;
        this._organicKeywordHistory[hashKey] = response.data;
      } else {
        response.data = this._organicKeywordHistory[hashKey] as OrganicKeywordHistoryResponse;
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error, handleError);
    }

    return response;
  }

  public async getOrganicTrendSummaryByProject(projectId: string, snapshotLimit: number = 12, handleError: boolean = false): Promise<{ data: OrganicTrendSummaryResponse | null; error: any }> {
    const response: { data: OrganicTrendSummaryResponse | null; error: any } = { data: null, error: null };
    const payload = { projectId, snapshotLimit };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._organicTrendSummary[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.get(`${environment.seoUrl}/serps/google/organicTrendSummary/project/${encodeURIComponent(projectId)}?snapshotLimit=${snapshotLimit}`)) as OrganicTrendSummaryResponse;
        this._organicTrendSummary[hashKey] = response.data;
      } else {
        response.data = this._organicTrendSummary[hashKey] as OrganicTrendSummaryResponse;
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error, handleError);
    }

    return response;
  }

  public async getOrganicRankRecommendationsByProject(projectId: string, snapshotLimit: number = 12, handleError: boolean = false): Promise<{ data: OrganicRankRecommendationsResponse | null; error: any }> {
    const response: { data: OrganicRankRecommendationsResponse | null; error: any } = { data: null, error: null };
    const payload = { projectId, snapshotLimit };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._organicRankRecommendations[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.get(`${environment.seoUrl}/serps/google/organicRankRecommendations/project/${encodeURIComponent(projectId)}?snapshotLimit=${snapshotLimit}`)) as OrganicRankRecommendationsResponse;
        this._organicRankRecommendations[hashKey] = response.data;
      } else {
        response.data = this._organicRankRecommendations[hashKey] as OrganicRankRecommendationsResponse;
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error, handleError);
    }

    return response;
  }

  public async queueOrganicRankRefresh(payload: OrganicRankRefreshRequest, handleError: boolean = false): Promise<{ data: OrganicRankRefreshResponse | null; error: any }> {
    const response: { data: OrganicRankRefreshResponse | null; error: any } = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/serps/google/organicRankRefresh`, payload)) as OrganicRankRefreshResponse;
    } catch (error) {
      response.error = error;
      await this.handleError(error, handleError);
    }

    return response;
  }

  public async runLocalSeoSweep(payload: { projectId: string; orgId: number; userId: string; localSeo: unknown }): Promise<{
    data: { pausedForCredits?: boolean; requiredCredits?: number; availableCredits?: number; failedSubmissions?: number } | null;
    error: unknown;
  }> {
    try {
      const data = await firstValueFrom(this.core.post(`${environment.seoUrl}/serps/localSeo/run`, payload));
      return { data: data as { pausedForCredits?: boolean; requiredCredits?: number; availableCredits?: number; failedSubmissions?: number }, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  public async runLocalSeoMapsScan(payload: any, handleError: boolean = false): Promise<{ data: any; error: any }> {
    const response = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/serps/google/maps`, payload));
    } catch (error) {
      response.error = error;
      await this.handleError(error, handleError);
    }

    return response;
  }

  public async runLocalSeoFinderScan(payload: any, handleError: boolean = false): Promise<{ data: any; error: any }> {
    const response = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/serps/google/localFinder`, payload));
    } catch (error) {
      response.error = error;
      await this.handleError(error, handleError);
    }

    return response;
  }

  public async generateLocalGeoGridCoordinates(payload: LocalGeoGridGenerateRequest, handleError: boolean = false): Promise<{ data: LocalGeoGridGenerateResponse | null; error: any }> {
    const response: { data: LocalGeoGridGenerateResponse | null; error: any } = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/seosettings/localSeo/grid/generate`, payload)) as LocalGeoGridGenerateResponse;
    } catch (error) {
      response.error = error;
      await this.handleError(error, handleError);
    }

    return response;
  }

  public async validateLocalGeoGridCoordinates(payload: LocalGeoGridValidateRequest, handleError: boolean = false): Promise<{ data: LocalGeoGridValidateResponse | null; error: any }> {
    const response: { data: LocalGeoGridValidateResponse | null; error: any } = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/seosettings/localSeo/grid/validate`, payload)) as LocalGeoGridValidateResponse;
    } catch (error) {
      response.error = error;
      await this.handleError(error, handleError);
    }

    return response;
  }

  public async getLocalSnapshotsByProjectAndKeyword(projectId: string, keyword: string, limit: number = 100, handleError: boolean = false): Promise<{ data: LocalSnapshot[] | null; error: any }> {
    const response: { data: LocalSnapshot[] | null; error: any } = { data: null, error: null };

    try {
      response.data = await firstValueFrom(this.core.get(`${environment.seoUrl}/serps/google/localSnapshots/project/${encodeURIComponent(projectId)}/keyword/${encodeURIComponent(keyword)}?limit=${limit}`)) as LocalSnapshot[];
    } catch (error) {
      response.error = error;
      await this.handleError(error, handleError);
    }

    return response;
  }

  public async getRelevantPages(payload: any, handleError: boolean = false) {
    const response = { data: null, error: null };
    const hashKey = this.utils.hashPayload(payload);
    const data = this._relevantPages[hashKey];

    try {
      if (!data) {
        response.data = await firstValueFrom(this.core.post(`${environment.seoUrl}/competitors/google/relevantPages`, payload));
        this._relevantPages[hashKey] = response.data;
      } else {
        response.data = this._relevantPages[hashKey];
      }
    } catch (error) {
      response.error = error;
      await this.handleError(error,handleError);
    }

    return response;
  }

  private getUserListPath(type: string): string {
    switch (type) {
      case UserListType.Keyword:
        return 'keywordUserList';
      case UserListType.Anchor:
        return 'anchorUserList';
      case UserListType.Backlink:
        return 'backLinkUserList';
      case UserListType.ReferringDomains:
        return 'referringDomainUserList';
      case UserListType.LinkIntersect:
        return 'linkIntersectUserList';
      default:
        return '';
    }
  }

  private handleError(error: any, handleError : boolean = false) {
    this.utils.logger.error(error);
    if (typeof error.error === 'object' && (error.error.code === 1010 || error.error.code === 1007)) {
      this.utils.errorMessage(MessageType.ALERT, error.error.title);
    } else {
        if (handleError) {
          this.utils.errorMessage(MessageType.ALERT, 'There was an error processing your request. Please try again later.');
        }
    }
  }

}
